import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Check, GripVertical, Megaphone } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Promotion { id: number; text: string; is_active: boolean; sort_order: number; }

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Promotion | null>(null);
  const [form, setForm] = useState({ text: '', is_active: true, sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/promotions');
      setPromotions(data);
    } catch {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditItem(null);
    setForm({ text: '', is_active: true, sort_order: promotions.length });
    setShowForm(true);
  };

  const openEdit = (p: Promotion) => {
    setEditItem(p);
    setForm({ text: p.text, is_active: p.is_active, sort_order: p.sort_order });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditItem(null); };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.text.trim()) { toast.error('Promotion text is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/admin/promotions/${editItem.id}`, form);
        toast.success('Promotion updated');
      } else {
        await api.post('/admin/promotions', form);
        toast.success('Promotion created');
      }
      closeForm();
      load();
    } catch {
      toast.error('Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p: Promotion) => {
    try {
      await api.put(`/admin/promotions/${p.id}`, { is_active: !p.is_active });
      setPromotions(prev => prev.map(x => x.id === p.id ? { ...x, is_active: !x.is_active } : x));
      toast.success(p.is_active ? 'Promotion hidden' : 'Promotion activated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await api.delete(`/admin/promotions/${id}`);
      toast.success('Promotion deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
            <Megaphone size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Promotions Ticker</h1>
            <p className="text-sm text-gray-500">Manage animated text displayed above the store header</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors"
        >
          <Plus size={16} /> Add Promotion
        </button>
      </div>

      {/* Live Preview */}
      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        <div className="bg-gray-700 text-white text-xs px-4 py-2 font-medium tracking-wider uppercase">Live Preview</div>
        <div className="bg-gradient-to-r from-pink-600 via-rose-500 to-orange-500 overflow-hidden py-2 relative">
          <style>{`
            @keyframes ticker-preview { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
            .ticker-preview-track { animation: ticker-preview 20s linear infinite; }
          `}</style>
          <div className="flex whitespace-nowrap ticker-preview-track">
            {[...(promotions.length > 0 ? promotions.filter(p => p.is_active).map(p => p.text) : ['Add a promotion above to see it here']),
              ...(promotions.length > 0 ? promotions.filter(p => p.is_active).map(p => p.text) : ['Add a promotion above to see it here'])
            ].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-3 px-6 text-white text-[11px] font-semibold tracking-[0.15em] uppercase">
                <span className="text-white/60">✦</span>{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : promotions.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Megaphone size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No promotions yet</p>
            <p className="text-sm mt-1">Add your first promotion to display in the animated ticker.</p>
            <button onClick={openCreate} className="mt-4 text-sm text-blue-600 hover:underline">+ Add Promotion</button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {/* Table Header */}
            <div className="grid grid-cols-[24px_1fr_80px_80px_100px] gap-4 px-5 py-3 bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <span></span>
              <span>Promotion Text</span>
              <span className="text-center">Order</span>
              <span className="text-center">Status</span>
              <span className="text-right">Actions</span>
            </div>
            {promotions.map(p => (
              <div key={p.id} className="grid grid-cols-[24px_1fr_80px_80px_100px] gap-4 px-5 py-4 items-center hover:bg-gray-50/50 transition-colors">
                <GripVertical size={16} className="text-gray-300 cursor-grab" />
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-100 to-orange-100 flex items-center justify-center flex-shrink-0">
                    <Megaphone size={14} className="text-pink-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate">{p.text}</span>
                </div>
                <span className="text-sm text-gray-500 text-center">{p.sort_order}</span>
                <div className="flex justify-center">
                  <button
                    onClick={() => toggleActive(p)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                      p.is_active
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {p.is_active ? <Check size={11} /> : <X size={11} />}
                    {p.is_active ? 'Active' : 'Hidden'}
                  </button>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => openEdit(p)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
                  <Megaphone size={17} className="text-white" />
                </div>
                <h2 className="text-base font-bold text-gray-900">
                  {editItem ? 'Edit Promotion' : 'New Promotion'}
                </h2>
              </div>
              <button onClick={closeForm} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={save} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Promotion Text *</label>
                <textarea
                  value={form.text}
                  onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                  placeholder="e.g. FREE DELIVERY ABOVE RS. 5,000 | USE CODE SUMMER20 FOR 20% OFF"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-400 transition-all resize-none"
                  required
                />
                <p className="text-xs text-gray-400 mt-1.5">This text will scroll in the animated ticker above your store header.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Sort Order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.sort_order}
                    onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-400 transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-1">Lower = shown first</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                      form.is_active
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}
                  >
                    {form.is_active ? <Check size={14} /> : <X size={14} />}
                    {form.is_active ? 'Active' : 'Hidden'}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 border border-gray-200 text-gray-700 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-pink-600 to-orange-500 text-white py-3 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                  {editItem ? 'Save Changes' : 'Create Promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
